import { XCircleIcon } from '@heroicons/react/24/outline'
import { forwardRef, useImperativeHandle } from 'react'

import UserContext from '@/gf/modules/User'

import Address from '@/gf/modules/Address'

import useToggle from '@/gf/hooks/useToggle'
import useSession from '../hooks/useSession'

import Card from '@/gf/components/Card'

import AddressComp from '@/gf/components/Address'
import AddressModalForm from '@/gf/components/AddressModalForm'
import CustomerInfoModalForm from './CustomerInfoModalForm'

import { CreateOrderFormCustomer, CustomerCardRef } from '../../types'

interface CustomerCardProps {
  orderCustomer: CreateOrderFormCustomer
  setOrderCustomer: (user?: CreateOrderFormCustomer) => void
}

const CustomerCard = forwardRef<CustomerCardRef, CustomerCardProps>(
  ({ orderCustomer, setOrderCustomer }, ref) => {
    const { user } = useSession()
    const [contactInfoModalOpen, contactInfoModalToggler] = useToggle()
    const [shippingAddressModalOpen, shippingAddressModalToggler] = useToggle()
    const [billingAddressModalOpen, billingAddressModalToggler] = useToggle()

    useImperativeHandle(ref, () => ({
      focusShippingAddress: () => {
        shippingAddressModalToggler.on()
      },
    }))

    return (
      <>
        {/* Edit customer info modal */}
        {orderCustomer && (
          <CustomerInfoModalForm
            initialCustomerInfo={{
              email: orderCustomer.email,
              phoneNumber: orderCustomer.phoneNumber ?? '',
            }}
            open={contactInfoModalOpen}
            onClose={contactInfoModalToggler.off}
            onSubmit={({ email, phoneNumber }) => {
              setOrderCustomer({
                ...orderCustomer,
                email,
                phoneNumber,
              })
              contactInfoModalToggler.off()
            }}
            showEmail={UserContext.isAdmin(user)}
          />
        )}

        {/* Edit shipping address modal */}
        {orderCustomer && (
          <AddressModalForm
            title="Edit shipping address"
            open={shippingAddressModalOpen}
            onClose={shippingAddressModalToggler.off}
            initialAddress={orderCustomer.shippingAddress}
            onSubmit={(newAddress) => {
              setOrderCustomer({
                ...orderCustomer,
                shippingAddress: {
                  ...orderCustomer.shippingAddress,
                  ...newAddress,
                },
              })
              shippingAddressModalToggler.off()
            }}
          />
        )}

        {/* Edit billing address modal */}
        {orderCustomer && (
          <AddressModalForm
            title="Edit billing address"
            open={billingAddressModalOpen}
            onClose={billingAddressModalToggler.off}
            initialAddress={orderCustomer.billingAddress || Address.init()}
            onSubmit={(newAddress) => {
              setOrderCustomer({
                ...orderCustomer,
                billingAddress: {
                  ...orderCustomer.billingAddress,
                  ...newAddress,
                },
              })
              billingAddressModalToggler.off()
            }}
          />
        )}

        <Card
          title="Customer"
          headerButton={
            <XCircleIcon
              className="h-6 w-6 cursor-pointer text-gray-500 hover:text-gray-900"
              onClick={() => {
                setOrderCustomer(undefined)
              }}
            />
          }
        >
          <Card.Section>
            <p className="text-sm text-gray-900">{orderCustomer.name}</p>
          </Card.Section>
          <Card.Section
            title="Contact Information"
            headerButton={
              <button
                className="text-sm text-blue-500 underline cursor-pointer"
                type="button"
                onClick={() => contactInfoModalToggler.on()}
              >
                Edit
              </button>
            }
          >
            {UserContext.isAdmin(user) && (
              <p className="text-sm text-gray-900">{orderCustomer.email}</p>
            )}
            {orderCustomer.phoneNumber ? (
              <p className="text-sm text-gray-900">{orderCustomer.phoneNumber}</p>
            ) : (
              <p className="text-sm text-gray-500">No phone number</p>
            )}
          </Card.Section>
          <Card.Section
            title="Shipping Address"
            headerButton={
              <button
                className="text-sm text-blue-500 underline cursor-pointer"
                type="button"
                onClick={() => shippingAddressModalToggler.on()}
              >
                Edit
              </button>
            }
          >
            {orderCustomer.shippingAddress ? (
              <AddressComp
                className="text-sm text-gray-900"
                address={orderCustomer.shippingAddress}
              />
            ) : (
              <p className="text-sm text-gray-500">No shipping address</p>
            )}
          </Card.Section>
          <Card.Section
            title="Billing Address"
            headerButton={
              <button
                className="text-sm text-blue-500 underline cursor-pointer"
                type="button"
                onClick={() => billingAddressModalToggler.on()}
              >
                Edit
              </button>
            }
          >
            {orderCustomer.billingAddress ? (
              <AddressComp
                className="text-sm text-gray-900"
                address={orderCustomer.billingAddress}
              />
            ) : orderCustomer.shippingAddress ? (
              <p className="text-sm text-gray-500">Same as shipping address</p>
            ) : (
              <p className="text-sm text-gray-500">No billing address</p>
            )}
          </Card.Section>
        </Card>
      </>
    )
  }
)

export default CustomerCard
